import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=DM+Sans:wght@300;400;500;600&display=swap');

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'DM Sans', sans-serif;
    background: #fff;
    color: #1a1a1a;
  }

  .enrich-root {
    font-family: 'DM Sans', sans-serif;
    min-height: 100vh;
  }

  /* NAV */
  .nav {
    background: #111;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 48px;
    height: 64px;
    position: sticky;
    top: 0;
    z-index: 100;
  }

  .nav-logo {
    display: flex;
    flex-direction: column;
    line-height: 1;
  }

  .nav-logo-name {
    font-family: 'Playfair Display', serif;
    font-size: 26px;
    font-weight: 700;
    color: #fff;
    letter-spacing: 1px;
    position: relative;
  }

  .nav-logo-name span {
    color: #e53935;
  }

  .nav-logo-tagline {
    font-size: 10px;
    color: #aaa;
    letter-spacing: 2px;
    text-transform: lowercase;
    margin-top: 2px;
  }

  .nav-links {
    display: flex;
    gap: 36px;
    list-style: none;
  }

  .nav-links a {
    color: #fff;
    text-decoration: none;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 7px;
    opacity: 0.85;
    transition: opacity 0.2s;
  }

  .nav-links a:hover {
    opacity: 1;
  }

  .nav-icon {
    font-size: 16px;
  }

  /* HERO / SLIDER */
  .slider {
    position: relative;
    height: 460px;
    overflow: hidden;
  }

  .slide {
    position: absolute;
    inset: 0;
    opacity: 0;
    transition: opacity 0.7s ease;
  }

  .slide.active {
    opacity: 1;
  }

  .slide-bg {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .slide-content {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    padding: 0 80px;
  }

  .slide-0 .slide-overlay {
    background: linear-gradient(120deg, #5b9bd5cc 0%, #88b8e888 50%, transparent 75%);
    position: absolute;
    inset: 0;
  }

  .slide-1 .slide-overlay {
    background: linear-gradient(120deg, #b85c8acc 0%, #d9845baa 55%, transparent 75%);
    position: absolute;
    inset: 0;
  }

  .slide-2 .slide-overlay {
    background: linear-gradient(120deg, #4caf7dcc 0%, #81c99faa 55%, transparent 75%);
    position: absolute;
    inset: 0;
  }

  .slide-3 .slide-overlay {
    background: linear-gradient(120deg, #7c5cbfcc 0%, #a07adaaa 55%, transparent 75%);
    position: absolute;
    inset: 0;
  }

  .slide-text {
    position: relative;
    z-index: 2;
    max-width: 550px;
  }

  .slide-text h1 {
    font-family: 'Playfair Display', serif;
    font-size: 62px;
    font-weight: 700;
    color: #fff;
    line-height: 1.1;
    text-shadow: 0 2px 16px rgba(0,0,0,0.18);
  }

  .slide-text p {
    font-size: 22px;
    color: rgba(255,255,255,0.92);
    margin-top: 10px;
    font-weight: 300;
  }

  .slide-products {
    position: absolute;
    right: 60px;
    bottom: 0;
    display: flex;
    align-items: flex-end;
    gap: 18px;
    height: 100%;
    padding-bottom: 0;
  }

  .product-bottle {
    height: 260px;
    object-fit: contain;
    filter: drop-shadow(0 8px 24px rgba(0,0,0,0.22));
    transition: transform 0.4s ease;
  }

  .product-bottle:hover {
    transform: translateY(-10px) scale(1.04);
  }

  .product-bottle.tall {
    height: 300px;
  }

  .slide-tcapply {
    position: absolute;
    bottom: 12px;
    right: 16px;
    font-size: 11px;
    color: rgba(255,255,255,0.7);
    z-index: 3;
  }

  /* DOTS */
  .slider-dots {
    display: flex;
    justify-content: center;
    gap: 10px;
    padding: 18px 0;
    background: #fff;
  }

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #ccc;
    border: none;
    cursor: pointer;
    transition: background 0.3s, transform 0.3s;
    padding: 0;
  }

  .dot.active {
    background: #e53935;
    transform: scale(1.2);
  }

  /* SERVICES */
  .section {
    padding: 52px 48px;
  }

  .section-title {
    font-family: 'Playfair Display', serif;
    font-size: 28px;
    font-weight: 700;
    margin-bottom: 28px;
    color: #111;
  }

  .services-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 18px;
  }

  .service-card {
    border-radius: 14px;
    border: 1.5px solid #e8e8e8;
    overflow: hidden;
    cursor: pointer;
    transition: box-shadow 0.25s, transform 0.25s;
    background: #fff;
  }

  .service-card:hover {
    box-shadow: 0 8px 32px rgba(0,0,0,0.12);
    transform: translateY(-4px);
  }

  .service-img {
    width: 100%;
    height: 170px;
    object-fit: cover;
    display: block;
    background: #f0f0f0;
  }

  .service-img-placeholder {
    width: 100%;
    height: 170px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 48px;
    background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
  }

  .service-label {
    padding: 12px 14px;
    font-size: 14px;
    font-weight: 600;
    color: #1a1a1a;
  }

  /* OFFERS */
  .offers-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }

  .offer-card {
    border-radius: 16px;
    padding: 28px 24px;
    position: relative;
    overflow: hidden;
    min-height: 140px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    cursor: pointer;
    transition: transform 0.25s, box-shadow 0.25s;
  }

  .offer-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 36px rgba(0,0,0,0.15);
  }

  .offer-card.blue {
    background: linear-gradient(135deg, #4a90d9 0%, #6cb4f5 100%);
    color: #fff;
  }

  .offer-card.rose {
    background: linear-gradient(135deg, #e8506a 0%, #f07f93 100%);
    color: #fff;
  }

  .offer-card.dark {
    background: linear-gradient(135deg, #1a1a2e 0%, #2d2d4e 100%);
    color: #fff;
  }

  .offer-title {
    font-family: 'Playfair Display', serif;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.2;
    margin-bottom: 6px;
  }

  .offer-sub {
    font-size: 13px;
    opacity: 0.88;
  }

  .offer-badge {
    position: absolute;
    top: 18px;
    right: 18px;
    background: rgba(255,255,255,0.22);
    border-radius: 8px;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 600;
  }

  /* FOOTER */
  .footer {
    background: #111;
    color: #aaa;
    padding: 40px 48px;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 32px;
    margin-top: 20px;
  }

  .footer-brand .nav-logo-name {
    font-size: 22px;
  }

  .footer-brand p {
    margin-top: 12px;
    font-size: 13px;
    line-height: 1.7;
  }

  .footer-col h4 {
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    margin-bottom: 16px;
  }

  .footer-col ul {
    list-style: none;
  }

  .footer-col ul li {
    margin-bottom: 10px;
    font-size: 13px;
    cursor: pointer;
    transition: color 0.2s;
  }

  .footer-col ul li:hover {
    color: #fff;
  }

  .footer-bottom {
    background: #0a0a0a;
    color: #555;
    text-align: center;
    padding: 16px;
    font-size: 12px;
  }

  @keyframes fadeSlideUp {
    from { opacity: 0; transform: translateY(24px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .slide.active .slide-text {
    animation: fadeSlideUp 0.6s ease forwards;
  }
`;

const slides = [
  {
    headline: "Get 20%\npoints back",
    sub: "on products",
    colorClass: "slide-0",
    products: [
      { label: "Kérastase Gloss", emoji: "🧴", color: "#f0c4b8" },
      { label: "L'Oréal Vitamino", emoji: "🍷", color: "#8b2635" },
      { label: "Thalgo Éveil", emoji: "🫧", color: "#a8d8d8" },
    ],
  },
  {
    headline: "Book Your\nSpa Day",
    sub: "exclusive member rates",
    colorClass: "slide-1",
    products: [
      { label: "Facial", emoji: "🌸", color: "#ffd6e0" },
      { label: "Massage Oil", emoji: "🫙", color: "#d4a373" },
    ],
  },
  {
    headline: "Hair Care\nElevated",
    sub: "premium salon services",
    colorClass: "slide-2",
    products: [
      { label: "Serum", emoji: "💚", color: "#90c97e" },
      { label: "Conditioner", emoji: "🌿", color: "#b8ddb0" },
    ],
  },
  {
    headline: "Luxury\nNail Art",
    sub: "express yourself",
    colorClass: "slide-3",
    products: [
      { label: "Nail Polish", emoji: "💅", color: "#c9b8f0" },
      { label: "Nail Set", emoji: "✨", color: "#e8d0f8" },
    ],
  },
];

const services = [
  { label: "Hair Services", emoji: "💇", bg: "#1a1a1a" },
  { label: "Skin & Facials", emoji: "🌿", bg: "#f5e6d3" },
  { label: "Body Treatments", emoji: "🛁", bg: "#e8f4f8" },
  { label: "Nail Art", emoji: "💅", bg: "#fce4ec" },
  { label: "Hair Extensions", emoji: "✨", bg: "#f3e5f5" },
];

const offers = [
  {
    title: "Get 20% Points Back",
    sub: "on all product purchases",
    badge: "Members",
    cls: "blue",
  },
  {
    title: "Book 2 Get 1 Free",
    sub: "on select services this month",
    badge: "Limited",
    cls: "rose",
  },
  {
    title: "Premium Membership",
    sub: "Unlock exclusive privileges",
    badge: "Enrich+",
    cls: "dark",
  },
];

export default function EnrichSalon() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setCurrent((c) => (c + 1) % slides.length), 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="enrich-root">
      <style>{styles}</style>

      {/* NAV */}
      <nav className="nav">
        <Link to="/" style={{ textDecoration: "none" }}>
          <div className="nav-logo">
            <div className="nav-logo-name">
              Style <span>S</span>utra
            </div>
            <div className="nav-logo-tagline">where beauty meets elegance</div>
          </div>
        </Link>
        <ul className="nav-links">
          <li><a href="#"><span className="nav-icon">🛍</span> Services</a></li>
          <li><a href="#"><span className="nav-icon">🏪</span> Stores</a></li>
          <li><a href="#"><span className="nav-icon">📋</span> About Us</a></li>
        </ul>
      </nav>

      {/* SLIDER */}
      <div className="slider">
        {slides.map((s, i) => (
          <div key={i} className={`slide ${s.colorClass} ${i === current ? "active" : ""}`}>
            <div className="slide-overlay" />
            <div className="slide-content">
              <div className="slide-text">
                <h1>{s.headline.split("\n").map((line, j) => (
                  <span key={j}>{line}{j === 0 && <br />}</span>
                ))}</h1>
                <p>{s.sub}</p>
              </div>
            </div>
            <div className="slide-products">
              {s.products.map((p, j) => (
                <div
                  key={j}
                  className={`product-bottle ${j === Math.floor(s.products.length / 2) ? "tall" : ""}`}
                  style={{
                    fontSize: j === Math.floor(s.products.length / 2) ? "120px" : "90px",
                    display: "flex",
                    alignItems: "flex-end",
                    paddingBottom: j === Math.floor(s.products.length / 2) ? "0" : "20px",
                    cursor: "default",
                    userSelect: "none",
                    filter: "drop-shadow(0 16px 32px rgba(0,0,0,0.3))",
                    transition: "transform 0.4s ease",
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = "translateY(-12px) scale(1.05)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "translateY(0) scale(1)"}
                >
                  {p.emoji}
                </div>
              ))}
            </div>
            <div className="slide-tcapply">*T&C Apply</div>
          </div>
        ))}
      </div>

      {/* DOTS */}
      <div className="slider-dots">
        {slides.map((_, i) => (
          <button key={i} className={`dot ${i === current ? "active" : ""}`} onClick={() => setCurrent(i)} />
        ))}
      </div>

      {/* SERVICES */}
      <div className="section">
        <h2 className="section-title">Services</h2>
        <div className="services-grid">
          {services.map((s, i) => (
            <div className="service-card" key={i}>
              <div className="service-img-placeholder" style={{ background: `linear-gradient(135deg, ${s.bg}22 0%, ${s.bg}44 100%)` }}>
                <span style={{ fontSize: 56 }}>{s.emoji}</span>
              </div>
              <div className="service-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* OFFERS */}
      <div className="section" style={{ paddingTop: 0 }}>
        <h2 className="section-title">Offers & Membership</h2>
        <div className="offers-grid">
          {offers.map((o, i) => (
            <div className={`offer-card ${o.cls}`} key={i}>
              <div className="offer-badge">{o.badge}</div>
              <div className="offer-title">{o.title}</div>
              <div className="offer-sub">{o.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* FOOTER */}
      <footer className="footer">
        <div className="footer-brand">
          <div className="nav-logo">
            <div className="nav-logo-name" style={{ color: "#fff" }}>Style <span>S</span>utra</div>
            <div className="nav-logo-tagline">where beauty meets elegance</div>
          </div>
          <p>SCO 16, Sector 2, Panchkula. Look good, feel great.</p>
        </div>
        <div className="footer-col">
          <h4>Services</h4>
          <ul>
            <li>Hair</li>
            <li>Skin & Facials</li>
            <li>Body Treatments</li>
            <li>Nail Art</li>
            <li>Bridal Packages</li>
          </ul>
        </div>
        <div className="footer-col">
          <h4>Quick Links</h4>
          <ul>
            <li>Find a Salon</li>
            <li>Book Appointment</li>
            <li>Membership</li>
            <li>Gift Cards</li>
          </ul>
        </div>
        <div className="footer-col">
          <h4>Connect</h4>
          <ul>
            <li>Instagram</li>
            <li>Facebook</li>
            <li>YouTube</li>
            <li>Contact Us</li>
          </ul>
        </div>
      </footer>
      <div className="footer-bottom">
        © 2025 Style Sutra Salon. All rights reserved. | <Link to="/terms" style={{ color: "#888", textDecoration: "none" }}>Terms & Conditions</Link> | <Link to="/privacy" style={{ color: "#888", textDecoration: "none" }}>Privacy Policy</Link>
      </div>
    </div>
  );
}